import { Modal as ModalAntd } from 'antd';
import { ModalFuncProps, ModalProps } from 'antd/es/modal';
import 'antd/es/modal/style/index';
import classnames from 'classnames';
import { isNumber } from 'lodash';
import React from 'react';
import { createRoot } from 'react-dom/client';
import ConfigProvider from 'antd/es/config-provider';
import IconFont from '@meitu/xdesign-icon-font';
import Button from '../button';
import { getLocaleStore } from '../i18n';
import './index.less';

interface IProps extends ModalProps {
    /**
     * 是否隐藏确认按钮
     */
    hideOkButton?: boolean;
    /**
     * 是否隐藏取消按钮
     */
    hideCancelButton?: boolean;
}

const ModalComponent: React.FC<IProps> = (props) => {
    const { className, children, ...restProps } = props;

    return (
        <ModalAntd className={classnames('mt-modal', className)} {...restProps}>
            {children}
        </ModalAntd>
    );
};

type ModalCustomizeType = typeof ModalComponent & {
    confirm: (config: IPropsConfirm) => void;
    checkClickOnModal: (target: HTMLElement) => boolean;
    useModal: (typeof ModalAntd)['useModal'];
};

const Modal = ModalComponent as ModalCustomizeType;

interface IPropsConfirm extends ModalFuncProps {
    hideIcon?: boolean;
    hideOkButton?: boolean;
    hideCancelButton?: boolean;
    centered?: boolean;
    closable?: boolean;
    maskClosable?: boolean;
    onInstance?: (instance: { destroy: () => void }) => void;
    verticalLayout?: boolean;
    /** 底部内容 */
    footer?: React.ReactNode;
}

Modal.checkClickOnModal = (target: HTMLElement) => {
    const classIndex = target?.className?.indexOf?.('ant-modal-wrap');

    if (isNumber(classIndex)) {
        return classIndex !== -1;
    }

    return false;
};

Modal.confirm = (props: IPropsConfirm) => {
    const { $t } = getLocaleStore();
    const div = document.createElement('div');
    document.body.appendChild(div);

    const root = createRoot(div);

    const destroy = () => {
        root.unmount();
        document.body.removeChild(div);
    };

    const {
        title,
        content,
        icon,
        hideIcon,
        hideOkButton,
        hideCancelButton,
        cancelText = $t((v) => v.modal.cancelText),
        okText = $t((v) => v.modal.okText),
        centered = false,
        closable = true,
        closeIcon = <IconFont fontSize={14} name="CrossBlack" />,
        onOk,
        onCancel,
        zIndex,
        className = '',
        onInstance,
        maskClosable = true,
        verticalLayout = false,
        footer = null,
    } = props;

    onInstance?.({
        destroy() {
            destroy();
        },
    });

    const onHandleOk = () => {
        if (onOk) onOk();

        destroy();
    };

    const onHandleCancel = (type: string) => {
        if (onCancel) onCancel(type);

        destroy();
    };

    root.render(
        <ModalAntd
            className={`mt-modal-confirm ${className}`}
            visible
            closeIcon={closeIcon}
            closable={closable}
            onCancel={() => onHandleCancel('close')}
            centered={centered}
            footer={false}
            zIndex={zIndex}
            maskClosable={maskClosable}
        >
            <div className="mt-modal-confirm__body">
                {title ? (
                    <div className="mt-modal-confirm__title">
                        {!hideIcon ? (
                            <div className="mt-modal-confirm__icon">
                                {icon ? icon : <IconFont fontSize={48} name="InfoCircleFill" />}
                            </div>
                        ) : null}
                        <div className="mt-modal-confirm__title__text">{title}</div>
                    </div>
                ) : null}
                {content ? <div className="mt-modal-confirm__content">{content}</div> : null}
                {!hideCancelButton || !hideOkButton ? (
                    <div className={classnames('mt-modal-confirm__actions', verticalLayout ? 'vertical' : '')}>
                        {!hideCancelButton ? (
                            <ConfigProvider autoInsertSpaceInButton={false}>
                                <Button
                                    className="mt-modal-confirm__cancel"
                                    type="text"
                                    size="large"
                                    onClick={() => onHandleCancel('cancel')}
                                >
                                    {cancelText}
                                </Button>
                            </ConfigProvider>
                        ) : null}
                        {!hideOkButton ? (
                            <ConfigProvider autoInsertSpaceInButton={false}>
                                <Button
                                    className="mt-modal-confirm__ok"
                                    type="primary"
                                    data-blue
                                    size="large"
                                    onClick={onHandleOk}
                                >
                                    {okText}
                                </Button>
                            </ConfigProvider>
                        ) : null}
                    </div>
                ) : null}
                {footer}
            </div>
        </ModalAntd>,
    );

    return { destroy };
};

export default Modal;
